import { clamp, round0 } from './formatters'

export type PackageType = '' | 'edge' | 'advantage' | 'summit'
export type BuildPayType = '' | 'upfront' | 'two'
export type IndustryType =
  | ''
  | 'Windows'
  | 'Roofing'
  | 'Solar (Sales)'
  | 'Solar (Vertical)'
  | 'Solar (Community)'
  | 'Pest'
  | 'Fiber'
  | 'Deregulated Energy'
  | 'Hardscaping'
  | 'Landscaping'
  | 'Other'

export interface Inputs {
  companyName: string
  pkg: PackageType
  buildPay: BuildPayType
  industry: IndustryType
  campaigns: number
  adSpend: number
  cph: number
  ticket: number
  gpm: number // 0-100 in state, converted to decimal for calc
  salesVetMonthly: number
  salesFirst90Total: number
  pctLaunched: number // 0-100
  pct30: number // 0-100
  pct90: number // 0-100
  pct1y: number // 0-100
  pullthrough: number // 0-100
}

export interface IndustryDefaults {
  ticket: number
  gpm: number
  salesFirst90Total: number
  salesVetMonthly: number
}

export const INDUSTRY_DEFAULTS: Record<IndustryType, IndustryDefaults> = {
  Windows: {
    ticket: 20000,
    gpm: 30,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
  },
  Roofing: {
    ticket: 20000,
    gpm: 20,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
  },
  'Solar (Sales)': {
    ticket: 40000,
    gpm: 10,
    salesFirst90Total: 6,
    salesVetMonthly: 6,
  },
  'Solar (Vertical)': {
    ticket: 40000,
    gpm: 20,
    salesFirst90Total: 6,
    salesVetMonthly: 6,
  },
  'Solar (Community)': {
    ticket: 350,
    gpm: 50,
    salesFirst90Total: 60,
    salesVetMonthly: 45,
  },
  Pest: {
    ticket: 850,
    gpm: 40,
    salesFirst90Total: 60,
    salesVetMonthly: 45,
  },
  Fiber: {
    ticket: 400,
    gpm: 50,
    salesFirst90Total: 60,
    salesVetMonthly: 45,
  },
  'Deregulated Energy': {
    ticket: 350,
    gpm: 50,
    salesFirst90Total: 60,
    salesVetMonthly: 45,
  },
  Hardscaping: {
    ticket: 20000,
    gpm: 25,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
  },
  Landscaping: {
    ticket: 50000,
    gpm: 30,
    salesFirst90Total: 5,
    salesVetMonthly: 8,
  },
  Other: {
    ticket: 25000,
    gpm: 20,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
  },
}

export interface PackagePreset {
  name: string
  buildTwoPayments: number
  buildUpfront: number
  systemMonthly: number
  campaignMonthly: number
  bonusLaunch: number
  bonusDay30: number
  bonusDay60: number
  bonusDay90: number
}

export const PACKAGE_PRESETS: Record<PackageType, PackagePreset> = {
  edge: {
    name: 'HIRE Edge',
    buildTwoPayments: 3000,
    buildUpfront: 3000,
    systemMonthly: 1500,
    campaignMonthly: 1500,
    bonusLaunch: 0,
    bonusDay30: 0,
    bonusDay60: 0,
    bonusDay90: 0,
  },
  advantage: {
    name: 'HIRE Advantage',
    buildTwoPayments: 3000,
    buildUpfront: 5000,
    systemMonthly: 2500,
    campaignMonthly: 2500,
    bonusLaunch: 250,
    bonusDay30: 250,
    bonusDay60: 250,
    bonusDay90: 250,
  },
  summit: {
    name: 'HIRE Summit',
    buildTwoPayments: 3000,
    buildUpfront: 5000,
    systemMonthly: 2500,
    campaignMonthly: 5500,
    bonusLaunch: 250,
    bonusDay30: 250,
    bonusDay60: 250,
    bonusDay90: 250,
  },
}

export interface RowData {
  period: string
  hires: number
  launched: number
  active30d: number
  active60d: number
  active90d: number
  veterans: number
  buildFee: number
  platformFee: number
  campaignFee: number
  hireCost: number
  adSpend: number
  bonuses: number
  totalCost: number
  totalSales: number
  realizationRate: number
  grossRevenue: number
  grossProfit: number
  netProfit: number
  cumProfit: number
}

export interface CalculationResult {
  preset: PackagePreset
  hiresPerMonth: number
  launchedPerMonth: number
  gpPerSale: number
  totalHireCost: number
  totalAdSpend: number
  totalBonuses: number
  totalCostAll: number
  totalGrossProfit: number
  totalNetProfit: number
  roi: number
  breakeven: string
  rows: RowData[]
}

export const DEFAULT_INPUTS: Inputs = {
  companyName: '',
  pkg: 'advantage',
  buildPay: 'upfront',
  industry: '',
  campaigns: 1,
  adSpend: 5000,
  cph: 300,
  ticket: 20000,
  gpm: 30,
  salesVetMonthly: 6,
  salesFirst90Total: 10,
  pctLaunched: 70,
  pct30: 30,
  pct90: 50,
  pct1y: 30,
  pullthrough: 80,
}

export function calculateModel(inputs: Inputs): CalculationResult {
  const months = 12
  const preset = PACKAGE_PRESETS[inputs.pkg]

  // Convert percentages from 0-100 inputs to 0-1 decimals
  const gpm = clamp(inputs.gpm / 100, 0, 1)
  const pctLaunched = clamp(inputs.pctLaunched / 100, 0, 1)
  const pct30 = clamp(inputs.pct30 / 100, 0, 1)
  const pct90 = clamp(inputs.pct90 / 100, 0, 1)
  const pct1y = clamp(inputs.pct1y / 100, 0, 1)
  const pullthrough = clamp(inputs.pullthrough / 100, 0, 1)

  const campaigns = Math.max(1, inputs.campaigns)
  const adSpendTotal = Math.max(0, inputs.adSpend) * campaigns
  const cph = Math.max(1, inputs.cph)

  const hiresPerMonth = round0(adSpendTotal / cph)
  const launchedPerMonth = hiresPerMonth * pctLaunched

  // Funnel per cohort (monthly cohorts)
  // Each cohort goes through: hired → launched → day30 → day60 → day90
  const hired = Array(months).fill(hiresPerMonth)
  const launched = hired.map((x) => x * pctLaunched)
  const cohortDay30 = launched.map((x) => x * pct30)

  // For accurate bonus calculation, we need proper day60 retention
  // pct90 represents retention from day 30 to day 90
  // We calculate day60 as the geometric mean: sqrt(pct90) retention from day30 to day60
  const pct60FromDay30 = Math.sqrt(pct90) // Retention rate from day 30 to day 60
  const cohortDay60 = cohortDay30.map((x) => x * pct60FromDay30)

  // Day 90 uses the remaining retention from day 60 to day 90
  const pct90FromDay60 = pct90 / pct60FromDay30 // Remaining retention from day 60 to day 90
  const cohortDay90 = cohortDay60.map((x) => x * pct90FromDay60)

  // Active counts by month
  // Month 1 hires → active at 30 days in Month 2
  const active30d = Array.from({ length: months }, (_, m) => {
    return m >= 1 ? cohortDay30[m - 1] : 0
  })

  // Month 1 hires → active at 60 days in Month 3
  const active60d = Array.from({ length: months }, (_, m) => {
    return m >= 2 ? cohortDay60[m - 2] : 0
  })

  // Month 1 hires → active at 90 days in Month 4
  const active90d = Array.from({ length: months }, (_, m) => {
    return m >= 3 ? cohortDay90[m - 3] : 0
  })

  // Veterans: reps who have been active for 90+ days (become veterans in month 5+)
  // Apply monthly attrition: 70% attrition over 9 months
  // Monthly attrition rate = 70% / 9 = 7.78% per month
  // Monthly retention rate = 1 - (7.78%) = 92.22%
  const monthlyVeteranAttritionRate = 0.7 / 9 // 7.78% attrition per month
  const monthlyVeteranRetentionRate = 1 - monthlyVeteranAttritionRate // 92.22% retention

  const veterans: number[] = []
  for (let m = 0; m < months; m++) {
    if (m < 4) {
      veterans.push(0) // No veterans until month 5 (index 4)
    } else {
      // Start with previous month's veterans and apply attrition
      const previousVeterans =
        m > 4 ? veterans[m - 1] * monthlyVeteranRetentionRate : 0

      // Add new graduates from 90 days (cohort from 3 months ago)
      const newGraduates = cohortDay90[m - 3]

      veterans.push(previousVeterans + newGraduates)
    }
  }

  // NEW SALES LOGIC: Sales are based on reps who will eventually reach 90 days
  // The total sales (salesFirst90Total) are split evenly across the 3 months
  // Each cohort's eventual 90-day graduates contribute 1/3 of their total sales each month
  const salesPer30Days = Math.max(0, inputs.salesFirst90Total) / 3

  // Calculate sales for each month based on cohorts that will eventually reach 90 days
  const totalSales = Array.from({ length: months }, (_, m) => {
    let sales = 0

    // Sales from cohorts in their first 30 days (will eventually be cohortDay90)
    // Month 2: Month 1 cohort in days 1-30 contributes 1/3 of eventual 90-day sales
    if (m >= 1) {
      const eventualGrads = cohortDay90[m - 1]
      sales += eventualGrads * salesPer30Days
    }

    // Sales from cohorts in their second 30 days (days 31-60)
    // Month 3: Month 2 cohort in days 31-60 contributes another 1/3
    if (m >= 2) {
      const eventualGrads = cohortDay90[m - 2]
      sales += eventualGrads * salesPer30Days
    }

    // Sales from cohorts in their third 30 days (days 61-90)
    // Month 4: Month 3 cohort in days 61-90 contributes final 1/3
    if (m >= 3) {
      const eventualGrads = cohortDay90[m - 3]
      sales += eventualGrads * salesPer30Days
    }

    // Sales from veterans (reps past 90 days)
    sales += veterans[m] * Math.max(0, inputs.salesVetMonthly)

    // Return total sales BEFORE pullthrough
    return sales
  })

  // Calculate realization (pullthrough applied to total sales)
  const realizedSales = totalSales.map((x) => x * pullthrough)

  // Calculate gross revenue (realized sales × ticket price)
  const grossRevenue = realizedSales.map((x) => x * Math.max(0, inputs.ticket))

  // Calculate gross profit (gross revenue × margin)
  const grossProfit = grossRevenue.map((x) => x * gpm)

  // Calculate gross profit per sale for summary stats
  const gpPerSale = Math.max(0, inputs.ticket) * gpm

  // Costs
  const buildLaunch =
    inputs.buildPay === 'upfront'
      ? preset.buildUpfront
      : preset.buildTwoPayments
  const buildM1 = inputs.buildPay === 'upfront' ? 0 : preset.buildTwoPayments
  const buildM2 = 0 // No third payment anymore

  const systemMonthly = preset.systemMonthly
  const campaignMonthly = preset.campaignMonthly * campaigns

  // Bonuses - ACCURATE calculation based on actual cohort progression
  const bonusLaunch = Array(months)
    .fill(0)
    .map((_, i) => preset.bonusLaunch * launched[i])

  // Day 30 bonus: paid when cohort reaches 30 days (month after launch)
  const bonusDay30 = Array(months)
    .fill(0)
    .map((_, m) => {
      const cohortIdx = m - 1
      return cohortIdx >= 0 ? preset.bonusDay30 * cohortDay30[cohortIdx] : 0
    })

  // Day 60 bonus: paid when cohort reaches 60 days (2 months after launch)
  const bonusDay60 = Array(months)
    .fill(0)
    .map((_, m) => {
      const cohortIdx = m - 2
      return cohortIdx >= 0 ? preset.bonusDay60 * cohortDay60[cohortIdx] : 0
    })

  // Day 90 bonus: paid when cohort reaches 90 days (3 months after launch)
  const bonusDay90 = Array(months)
    .fill(0)
    .map((_, m) => {
      const cohortIdx = m - 3
      return cohortIdx >= 0 ? preset.bonusDay90 * cohortDay90[cohortIdx] : 0
    })

  const bonuses = bonusLaunch.map(
    (x, i) => x + bonusDay30[i] + bonusDay60[i] + bonusDay90[i],
  )

  // Monthly HIRE cost - now separated into components
  const buildFees = Array(months)
    .fill(0)
    .map((_, i) => {
      if (i === 0) return buildM1 // Month 1: second payment (if split)
      if (i === 1) return buildM2 // Month 2: third payment (if applicable)
      return 0
    })

  const platformFees = Array(months).fill(systemMonthly)
  const campaignFees = Array(months).fill(campaignMonthly)

  const hireCost = Array(months)
    .fill(0)
    .map((_, i) => buildFees[i] + platformFees[i] + campaignFees[i])

  const adSpend = Array(months).fill(adSpendTotal)
  const totalCost = Array(months)
    .fill(0)
    .map((_, i) => hireCost[i] + adSpend[i] + bonuses[i])
  const netProfit = grossProfit.map((x, i) => x - totalCost[i])

  // Rows
  const rows: RowData[] = []
  let cum = -buildLaunch

  rows.push({
    period: 'Build',
    hires: 0,
    launched: 0,
    active30d: 0,
    active60d: 0,
    active90d: 0,
    veterans: 0,
    buildFee: buildLaunch,
    platformFee: 0,
    campaignFee: 0,
    hireCost: buildLaunch,
    adSpend: 0,
    bonuses: 0,
    totalCost: buildLaunch,
    totalSales: 0,
    realizationRate: pullthrough,
    grossRevenue: 0,
    grossProfit: 0,
    netProfit: -buildLaunch,
    cumProfit: cum,
  })

  for (let i = 0; i < months; i++) {
    cum += netProfit[i]
    rows.push({
      period: `Month ${i + 1}`,
      hires: hired[i],
      launched: launched[i],
      active30d: active30d[i],
      active60d: active60d[i],
      active90d: active90d[i],
      veterans: veterans[i],
      buildFee: buildFees[i],
      platformFee: platformFees[i],
      campaignFee: campaignFees[i],
      hireCost: hireCost[i],
      adSpend: adSpend[i],
      bonuses: bonuses[i],
      totalCost: totalCost[i],
      totalSales: totalSales[i],
      realizationRate: pullthrough,
      grossRevenue: grossRevenue[i],
      grossProfit: grossProfit[i],
      netProfit: netProfit[i],
      cumProfit: cum,
    })
  }

  const totalHireCost = buildLaunch + hireCost.reduce((a, b) => a + b, 0)
  const totalAdSpend = adSpend.reduce((a, b) => a + b, 0)
  const totalBonuses = bonuses.reduce((a, b) => a + b, 0)
  const totalCostAll = totalHireCost + totalAdSpend + totalBonuses
  const totalGrossProfit = grossProfit.reduce((a, b) => a + b, 0)
  const totalNetProfit =
    totalGrossProfit -
    (hireCost.reduce((a, b) => a + b, 0) + totalAdSpend + totalBonuses) -
    buildLaunch
  const roi = totalCostAll > 0 ? (totalNetProfit / totalCostAll) * 100 : 0

  let breakeven = 'Not within 12 months'
  for (let i = 0; i < rows.length; i++) {
    if (rows[i].cumProfit >= 0) {
      breakeven = rows[i].period
      break
    }
  }

  return {
    preset,
    hiresPerMonth,
    launchedPerMonth,
    gpPerSale,
    totalHireCost,
    totalAdSpend,
    totalBonuses,
    totalCostAll,
    totalGrossProfit,
    totalNetProfit,
    roi,
    breakeven,
    rows,
  }
}
